import Link from '@/components/compat/Link';
import { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import {
  GuidePremiumBadge,
  GuidePremiumCardShell,
} from '@/components/help-center/GuidePremiumCard';
import { useCms } from '@/components/painel/cms/cms-context';
import { allArticles } from '@/lib/articles';
import { getCmsArticleSlug } from '@/lib/cms-guides';
import { HOME_PAGE_CATEGORY_COLOR_CLASSES } from '@/lib/help-center-home-categories';
import { getGuideCategoryHref, getGuideHref } from '@/lib/guide-routes';

/** Ordem fixa dos cards «Continue explorando» (esquerda → direita). */
const FEATURED_GUIDES = [
  {
    category: 'account',
    slug: 'como-criar-uma-conta-no-xat',
    iconSrc: '/icons/user.svg',
    icon: 'UserSvg',
  },
  {
    category: 'primeiros-passos',
    slug: 'enviando-sua-primeira-mensagem',
  },
  {
    category: 'primeiros-passos',
    slug: 'smilies-os-emojis-do-xat',
    iconSrc: '/icons/smile.svg',
    icon: 'Smile',
  },
  {
    category: 'powers',
    slug: 'como-comprar-powers',
    iconSrc: '/icons/power.svg',
    icon: 'PowerSvg',
  },
] as const;

export default function PopularArticles() {
  const { categories, articles } = useCms();

  const categoryMeta = useMemo(() => {
    const m = new Map<string, { name: string; icon: string; color: string }>();
    for (const c of categories) {
      m.set(c.id, {
        name: c.name,
        icon: c.icon,
        color: c.color && HOME_PAGE_CATEGORY_COLOR_CLASSES[c.color] ? c.color : 'sky',
      });
    }
    return m;
  }, [categories]);

  const cmsByKey = useMemo(() => {
    const m = new Map<string, (typeof articles)[number]>();
    for (const a of articles) {
      const slug = getCmsArticleSlug(a);
      if (slug) m.set(`${a.categoryId}:${slug}`, a);
    }
    return m;
  }, [articles]);

  const guides = useMemo(() => {
    const staticByKey = new Map(allArticles.map((a) => [`${a.category}:${a.slug}`, a]));

    return FEATURED_GUIDES.flatMap((featured) => {
      const key = `${featured.category}:${featured.slug}`;
      const cms = cmsByKey.get(key);
      const fallback = staticByKey.get(key);
      if (!cms && !fallback) return [];

      const meta = categoryMeta.get(featured.category);
      const title = cms?.title ?? fallback!.title;
      const cmsIcon = cms?.icon?.trim() || null;
      const featuredIcon = 'icon' in featured ? featured.icon : undefined;
      const featuredIconSrc = 'iconSrc' in featured ? featured.iconSrc : undefined;

      return [
        {
          key,
          title,
          categoryName: meta?.name ?? fallback?.categoryName ?? featured.category,
          colorKey: meta?.color ?? 'sky',
          href: getGuideHref(featured.category, featured.slug),
          icon: cmsIcon || featuredIcon || meta?.icon || 'FileText',
          // Só usa SVG hardcoded se o guia ainda não tiver ícone no CMS
          iconSrc: cmsIcon ? undefined : featuredIconSrc,
        },
      ];
    });
  }, [categoryMeta, cmsByKey]);

  return (
    <section
      className="border-t border-gray-100 bg-gradient-to-b from-gray-50/90 to-white px-6 py-12 md:py-14"
      aria-labelledby="continue-exploring-heading"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
        <header className="max-w-2xl space-y-2">
          <h2
            id="continue-exploring-heading"
            className="font-poppins text-2xl font-bold tracking-tight text-gray-900 md:text-3xl"
          >
            Continue explorando
          </h2>
          <p className="font-poppins text-sm leading-relaxed text-gray-500 md:text-[15px]">
            Os guias mais acessados pela comunidade para aprender mais sobre o universo do xat.
          </p>
        </header>

        <ul
          className="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-4"
          role="list"
        >
          {guides.map((item) => (
            <li key={item.key} className="min-w-0">
              <Link href={item.href} className="block h-full">
                <GuidePremiumCardShell
                  colorKey={item.colorKey}
                  icon={item.icon}
                  iconSrc={item.iconSrc}
                  title={item.title}
                  badge={<GuidePremiumBadge colorKey={item.colorKey}>{item.categoryName}</GuidePremiumBadge>}
                  footer={
                    <span className="font-medium text-gray-500 transition-colors group-hover:text-gray-700">
                      Ver guia
                    </span>
                  }
                />
              </Link>
            </li>
          ))}
        </ul>

        <div className="flex justify-start">
          <Link
            href={getGuideCategoryHref('primeiros-passos')}
            className="group inline-flex items-center gap-1.5 font-poppins text-sm font-semibold text-sky-600 transition-colors hover:text-sky-700"
          >
            Ver todos os guias
            <ArrowRight
              className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
              strokeWidth={2.5}
            />
          </Link>
        </div>
      </div>
    </section>
  );
}
